import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './Monitor.module.scss';

import { Cell, Row } from '@/components/ui/dashboard/Monitor';
import { LANDED_STATE, GPS_FIX_TYPE } from '@/define/robotStatus';
import { getDistance } from '@/utils/MapUtils';

const cx = classNames.bind(styles);

const Monitor = ({ robotId }) => {
  const heartbeat = useSelector((state) => state.telemetry[robotId]?.heartbeat);
  const globalPosition = useSelector((state) => state.telemetry[robotId]?.globalPosition);
  const gpsRaw = useSelector((state) => state.telemetry[robotId]?.gpsRaw);
  const vfrHud = useSelector((state) => state.telemetry[robotId]?.vfrHud);
  const cameraFeedback = useSelector((state) => state.telemetry[robotId]?.cameraFeedback);
  const homePosition = useSelector((state) => state.telemetry[robotId]?.homePosition);
  const sysState = useSelector((state) => state.telemetry[robotId]?.extendedSysState);

  // Arm State
  const { isArm, armState } = useMemo(() => {
    const isArm = heartbeat?.isArm ?? false;
    return { isArm, armState: isArm ? 'ARM' : 'DISARM' };
  }, [heartbeat]);

  // Distance to Home
  const distanceToHome = useMemo(() => {
    if (!globalPosition) return 0;
    if (!homePosition) return 0;

    return getDistance(globalPosition, homePosition);
  }, [globalPosition, homePosition]);

  // Shoot Count
  const imageCount = useMemo(() => {
    return cameraFeedback?.imgIdx ?? 0;
  }, [cameraFeedback]);

  // Relative Altitude, MSL Altitude
  const { ralt, alt } = useMemo(() => {
    return {
      ralt: globalPosition?.ralt ?? 0,
      alt: globalPosition?.alt ?? 0,
    };
  }, [globalPosition]);

  // Throttle, Airspeed, Ground Speed, Vertical Speed
  const { throttle, airspeed, groundspeed, climb } = useMemo(() => {
    return {
      throttle: vfrHud?.throttle ?? 0,
      airspeed: vfrHud?.airspeed ?? 0,
      groundspeed: vfrHud?.groundspeed ?? 0,
      climb: vfrHud?.climb ?? 0,
    };
  }, [vfrHud]);

  // #region Total Distance Traveled
  const prevTimeRef = useRef(null);
  const accumulatedDistance = useRef(0);
  const [totalDistance, setTotalDistance] = useState(0);

  useEffect(() => {
    if (!vfrHud) return;

    // DISARM 상태에서는 거리 누적 중단
    if (!isArm) {
      prevTimeRef.current = null;
      return;
    }

    const currentTime = Date.now();

    if (prevTimeRef.current !== null) {
      const timeDiff = (currentTime - prevTimeRef.current) / 1000; // 시간 단위 변환
      const distance = groundspeed * timeDiff; // m/s * s = m

      accumulatedDistance.current += distance;
      setTotalDistance(accumulatedDistance.current);
    }

    prevTimeRef.current = currentTime;
  }, [vfrHud, isArm]);
  // #endregion

  return (
    <div className={cx('container')}>
      <div className={cx('rows')}>
        <Row label="Arm State" value={armState} valueStyle={!isArm && { color: 'red' }} />
        <Row label="VTOL State" value={sysState?.isFixedWing ? 'Fixed-wing' : 'Multicopter'} s />
        <Row label="Landed State" value={LANDED_STATE[sysState?.landedState]} />
        <Row label="GPS Fix Type" value={gpsRaw ? GPS_FIX_TYPE[gpsRaw.fixType].name : '-'} />
        {imageCount > 0 && <Row label="Shoot Count" value={commaNumber(imageCount)} />}
      </div>
      <div className={cx('boxes')}>
        <Cell data={[{ title: 'Distance to Home', value: commaNumber(distanceToHome.toFixed(2)), unit: 'm' }]} />
        <Cell data={[{ title: 'Total Distance', value: commaNumber(totalDistance.toFixed(2)), unit: 'm' }]} />
      </div>
      <div className={cx('boxes')}>
        <Cell data={[{ title: 'Throttle', value: throttle.toFixed(1), unit: '%' }]} />
        <Cell
          data={[
            { title: 'Relative Alt.', value: commaNumber(ralt.toFixed(2)), unit: 'm' },
            { title: 'MSL Altitude', value: commaNumber(alt.toFixed(2)), unit: 'm' },
          ]}
        />
        <Cell
          data={[
            { title: 'Airspeed', value: commaNumber(airspeed.toFixed(2)), unit: 'm/s' },
            { title: 'Ground Speed', value: commaNumber(groundspeed.toFixed(2)), unit: 'm/s' },
            { title: 'Vertical Speed', value: commaNumber(climb.toFixed(2)), unit: 'm/s' },
          ]}
        />
      </div>
    </div>
  );
};

export default Monitor;
